/**
 * platform-login-page.tsx — Đăng nhập super-admin (khu vực Platform).
 * Route `/platform/login`. Dùng token platform riêng, KHÔNG đụng tới auth CRM.
 */
import { useEffect, useState } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { Loader2, ShieldCheck } from 'lucide-react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Card } from '@/components/ui/card'
import { usePlatformAuthStore } from '@/stores/platform-auth-store'
import { platformApiError } from '@/lib/platform-client'
import { captchaPendingMessage } from '@/lib/turnstile'
import { TurnstileBox, useTurnstile } from '@/components/shared/turnstile-box'
import { useSignupConfig } from '@/pages/auth/auth-api'
import { TotpLoginStep } from '@/pages/auth/totp-login-step'
import type { TotpChallenge } from '@/types/api'

export function PlatformLoginPage() {
  const login = usePlatformAuthStore((s) => s.login)
  const admin = usePlatformAuthStore((s) => s.admin)
  const completeTotpLogin = usePlatformAuthStore((s) => s.completeTotpLogin)
  const startTotpLoginSetup = usePlatformAuthStore((s) => s.startTotpLoginSetup)
  const confirmTotpLoginSetup = usePlatformAuthStore((s) => s.confirmTotpLoginSetup)
  const navigate = useNavigate()
  const location = useLocation()
  const { config } = useSignupConfig()
  const ts = useTurnstile(config?.turnstileSiteKey, 'platform-login')

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [loading, setLoading] = useState(false)
  // Mật khẩu đúng nhưng còn thiếu bước TOTP — BẮT BUỘC cho mọi PlatformAdmin.
  const [totpChallenge, setTotpChallenge] = useState<TotpChallenge | null>(null)

  const from = (location.state as { from?: { pathname: string } })?.from?.pathname || '/platform'

  // Nếu đã đăng nhập platform thì vào thẳng console.
  useEffect(() => {
    if (admin) navigate(from, { replace: true })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [admin])

  function onDone() {
    toast.success('Đăng nhập Platform thành công')
    navigate(from, { replace: true })
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (ts.pending) return toast.error(captchaPendingMessage())
    setLoading(true)
    try {
      const result = await login(email.trim(), password, ts.token)
      if ('totpRequired' in result) setTotpChallenge(result)
      else onDone()
    } catch (err) {
      toast.error(platformApiError(err))
      ts.reset() // token Turnstile dùng được một lần
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-primary to-primary/70 p-4">
      <Card className="w-full max-w-md p-8">
        <div className="mb-6 flex flex-col items-center text-center">
          <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10 text-primary">
            <ShieldCheck className="h-6 w-6" />
          </div>
          <h1 className="mt-3 text-lg font-bold tracking-tight">Platform Console</h1>
          <p className="mt-1 text-sm text-muted-foreground">Quản trị đa tổ chức (super-admin)</p>
        </div>

        {totpChallenge ? (
          <TotpLoginStep
            challenge={totpChallenge}
            onVerify={completeTotpLogin}
            onSetupStart={startTotpLoginSetup}
            onSetupConfirm={confirmTotpLoginSetup}
            onDone={onDone}
            onBack={() => setTotpChallenge(null)}
            errorMessage={platformApiError}
          />
        ) : (
          <form onSubmit={onSubmit} className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                autoComplete="username"
                placeholder="admin@ecdp.vn"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password">Mật khẩu</Label>
              <Input
                id="password"
                type="password"
                autoComplete="current-password"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </div>
            <TurnstileBox ts={ts} />
            <Button type="submit" className="w-full" disabled={loading || ts.pending}>
              {loading && <Loader2 className="h-4 w-4 animate-spin" />}
              Đăng nhập
            </Button>
          </form>
        )}
      </Card>
    </div>
  )
}
